
import { Component, Vue, Watch } from 'vue-property-decorator';
import { getAddressLookupData, getQuantListData, lookupCustomer } from '@/api';

import Header from '@/components/jobBooking/Header/Header.vue';
import LookupCustomerCode from '@/components/jobBooking/AdvancedJobBooking/LookupCustomerCode.vue';
import Button from '@/components/UI/button/Button.vue';
import BackButton from '@/components/UI/backButton/BackButton.vue';

import { formatDateStringWithTZ } from '@/utils/InputProcessingUtilities';
import { notificationErrorText, timeZoneDefault } from '@/utils/PortalConstants';

import { QuantListData, QuantList, TQuantDataParams, QPagination } from '@/models/jobQuants';
import { User } from '@/models/main';
import { JBAdvancedStopProducts } from '@/models/jbAdvanced';
import {
  AddressBookAirRoadStop,
  AddressBookEndpointParam,
  AddressBookLookupData,
  AddressBookResponse,
} from '@/models/addressBook';

import { fetchGetAddressBookAddresses } from '@/helpers/fetchers';
import * as routeNames from '@/router/routeNames';
import { mapState } from 'vuex';
import { JobProducts } from '@/models/jobData';
import CustomerCodeSelect from '@/components/dashBoard/selectsFields/CustomerCodeSelect/CustomerCodeSelect.vue';

@Component({
  components: {
    BackButton,
    Button,
    CustomerCodeSelect,
    Header,
    LookupCustomerCode,
  },
  computed: {
    ...mapState('moduleUser', { user: 'user' }),
  },
})
export default class QuantPage extends Vue {
  // LOADERS
  public loading = false;
  public wareHouse = '';
  public addressesWarehouses: AddressBookAirRoadStop[] = [];

  // USER
  public user!: User;
  public timeZoneDefault = timeZoneDefault;

  // API
  public autocompleteCustomer = lookupCustomer;

  // HEADER DATA
  public headerFilters = {
    customerCode: '',
    quantIds: '',
    warehouse: '',
    product: '',
    reference1: '',
    reference2: '',
    condition: 'new',
    serialNumber: '',
    changed: false,
  };

  // MODAL
  public quantIdsModalOpen = false;
  public tempQuantIds = '';

  public sortField = '';
  public sortDirection: 'asc' | 'desc' = 'asc';
  public tableColumns = [
    { field: 'id', label: 'ID', width: 'w-16' },
    { field: 'condition', label: 'Condition', width: 'w-20' },
    { field: 'product_id', label: 'Product', width: 'w-48' },
    { field: 'qty', label: 'Qty', width: 'w-12' },
    { field: 'warehouse_code', label: 'WH Code', width: 'w-20' },
    { field: 'reference', label: 'Ref', width: 'w-24' },
    { field: 'reference2', label: 'Ref 2', width: 'w-32' },
    { field: 'owner_id', label: 'Owner', width: 'w-16' },
    { field: 'lot_id', label: 'Serial', width: 'w-32' },
    { field: 'in_date', label: 'Date', width: 'w-20' },
  ];
  public conditionList = ['new', 'used', 'excellent', 'good', 'poor'];
  public whList = {
    'KK Brisbane': 'BNE1',
    'KK Adelaide': 'ADL1',
    'KK Perth': 'PER-R',
    'KK Melbourne': 'MEL3',
    'KK Darwin': 'NT1',
    'KK Canberra': 'CBR1',
    'KK Sydney': 'SYD1',
    'KK SWH2': 'SWH2',
    'KK MWH2': 'MWH2',
    'KK Townsville': 'TSV',
  };

  private ricohWarehouseAddress: AddressBookLookupData = {
    city: null,
    line1: '',
    line2: null,
    line3: null,
    name: '',
    suburb: {
      id: 0,
      name: '',
      postcode: '',
      state: '',
    },
    id: 0,
    note_text: '',
    note_name: '',
    note_phone: '',
    warehouse: false,
  };

  // HEADER FILTERS
  public clearWarehouseClicked() {
    this.headerFilters.warehouse = '';
  }

  public headerFilterChanged() {
    this.headerFilters.changed = true;
  }

  private getAddressBookWrapper(): void {
    const params: AddressBookEndpointParam = {
      username: '',
      customer: this.headerFilters.customerCode,
      page: 1,
      warehouse: true,
    };
    fetchGetAddressBookAddresses(params).then((res: AddressBookResponse) => {
      this.addressesWarehouses = res.results;
    });
  }

  // PAGE DATA
  private pageData: QuantListData = {
    count: 0,
    next: '',
    previous: '',
    results: [],
  };

  // PAGINATION
  public pagination: QPagination = {
    page: 1,
    current: 1,
    total: 0,
    perPage: '50',
    visible: 7,
    perPageOptions: ['1', '5', '10', '50', '100'],
  };

  public tableData: QuantList[] = [];

  get cart(): QuantList[] {
    return this.$store.state.moduleQuantCart.items;
  }

  get cartCount(): number {
    return this.$store.getters['moduleQuantCart/cartCount'];
  }

  get cartWarehouse(): string {
    return this.$store.getters['moduleQuantCart/cartWarehouse'];
  }

  get cartItemIds(): number[] {
    return this.$store.getters['moduleQuantCart/cartItemIds'];
  }

  // CART METHODS
  public addToCart(item: QuantList): void {
    this.$store.dispatch('moduleQuantCart/addItem', item);
  }

  public removeFromCart(itemId: number): void {
    this.$store.dispatch('moduleQuantCart/removeItem', itemId);
  }

  public clearCart(): void {
    this.$store.dispatch('moduleQuantCart/clearCart');
  }

  public isInCart(itemId: number): boolean {
    return this.$store.getters['moduleQuantCart/isInCart'](itemId);
  }

  get allCheckboxesSelected(): boolean {
    if (!this.tableData || this.tableData.length === 0 || !this.hasOneWarehouse) {
      return false;
    }
    const selectableQuants = this.tableData.filter(
      item => item.warehouse_code !== 'SWH2' && item.warehouse_code !== 'MWH2',
    );
    if (selectableQuants.length === 0) {
      return false;
    }
    return selectableQuants.every(item => this.isInCart(item.id));
  }

  get hasOneWarehouse(): boolean {
    if (!this.tableData || this.tableData.length === 0) {
      return false;
    }
    const uniqueWarehouses = new Set(this.tableData.map(item => item.warehouse_code));
    return uniqueWarehouses.size === 1;
  }

  public toggleAllCheckboxes(): void {
    if (!this.tableData || this.tableData.length === 0 || !this.hasOneWarehouse) {
      return;
    }

    const selectableQuants = this.tableData.filter(
      item => item.warehouse_code !== 'SWH2' && item.warehouse_code !== 'MWH2',
    );

    if (selectableQuants.length === 0) {
      return;
    }

    const allCheckboxesSelected = selectableQuants.every(item => this.isInCart(item.id));

    if (allCheckboxesSelected) {
      selectableQuants.forEach(item => {
        if (this.isInCart(item.id)) {
          this.removeFromCart(item.id);
        }
      });
    } else {
      selectableQuants.forEach(item => {
        if (!this.isInCart(item.id)) {
          this.addToCart(item);
        }
      });
    }
  }

  // WRAPPERS
  public async getQuantWrapper(): Promise<void> {
    this.loading = true;
    this.pageData = { count: 0, next: '', previous: '', results: [] };
    this.tableData = [];

    if (this.headerFilters.changed) {
      this.pagination.current = 1;
    }
    const chosenWh = this.whList[this.headerFilters.warehouse];
    let cleanedQuantIds = '';
    if (this.headerFilters.quantIds) {
      const idsArray = this.headerFilters.quantIds
        .split(/[\s,]+/)
        .map(id => id.trim())
        .filter(id => id.length > 0 && /^\d+$/.test(id));
      cleanedQuantIds = idsArray.join(',');
    }
    const endpointParams: TQuantDataParams = {
      customer_code: this.headerFilters.customerCode,
      page: this.pagination.current,
      page_size: Number(this.pagination.perPage),
      in_date_gte: '',
      in_date_lte: '',
      warehouse_code: chosenWh,
      product_name: this.headerFilters.product,
      reference: this.headerFilters.reference1,
      reference2: this.headerFilters.reference2,
      condition: this.headerFilters.condition,
      serial_number: this.headerFilters.serialNumber,
      ordering: this.sortField ? `${this.sortDirection === 'desc' ? '-' : ''}${this.sortField}` : undefined,
      id: cleanedQuantIds,
    };
    await getQuantListData(endpointParams)
      .then((res: QuantListData) => {
        this.pageData = res;
        this.tableData = res.results;
        this.pagination.total = Math.ceil(res.count / Number(this.pagination.perPage));
      })
      .catch(() => {
        this.$notify({
          group: 'network',
          title: 'Error loading Quants data',
          text: notificationErrorText,
          type: 'warn',
        });
      })
      .finally(() => {
        this.headerFilters.changed = false;
        this.loading = false;
      });
  }

  // CUSTOMER CODE
  public async setCustomerCode(customerCode: string): Promise<void> {
    if (customerCode) {
      this.headerFilters.customerCode = customerCode;
      this.pagination.current = 1;
    }
  }
  public clearCustomerCode(): void {
    this.headerFilters.customerCode = '';
    this.pagination.current = 1;
  }

  // Row checkbox
  public jobCheckboxClicked(item: QuantList) {
    if (this.isInCart(item.id)) {
      this.removeFromCart(item.id);
    } else {
      this.addToCart(item);
    }
  }

  public ProcessBookingClick() {
    this.$store.dispatch('moduleQuantCart/updateCustomerCode', this.headerFilters.customerCode);
    this.$router.push({ name: routeNames.QUANTS_CART_PAGE, query: { fromQuantPage: 'true' } });
  }

  public openQuantIdsModal() {
    this.tempQuantIds = this.headerFilters.quantIds;
    this.quantIdsModalOpen = true;
  }

  public closeQuantIdsModal() {
    this.quantIdsModalOpen = false;
    this.tempQuantIds = '';
  }

  public applyQuantIds() {
    const idsArray = this.tempQuantIds
      .split(/[\s,]+/)
      .map(id => id.trim())
      .filter(id => id.length > 0 && /^\d+$/.test(id));
    this.headerFilters.quantIds = idsArray.join(', ');
    this.closeQuantIdsModal();
    this.headerFilterChanged();
  }

  public formatDateStringWithTZ = formatDateStringWithTZ;

  public setSorting(field: string) {
    if (this.sortField === field) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDirection = 'asc';
    }
    const wasOnPageOne = this.pagination.current === 1;
    this.pagination.current = 1;
    if (wasOnPageOne) {
      this.updatePagination();
    }
  }

  // Pagination
  public async updatePagination() {
    this.headerFilters.changed = false;
    await this.getQuantWrapper();
    this.pagination.total = Math.ceil(this.pageData.count / Number(this.pagination.perPage));
  }

  // Watchers:
  //
  // Pagination
  @Watch('pagination.current')
  private paginationCurrentChanged() {
    this.updatePagination();
  }

  @Watch('pagination.perPage')
  private perPageSelectChanged() {
    // Don't clear cart on page size change
    const wasOnPageOne = this.pagination.current === 1;
    this.pagination.current = 1;
    if (wasOnPageOne) {
      this.updatePagination();
    }
  }

  private mounted() {
    // Load cart from localStorage
    this.$store.dispatch('moduleQuantCart/loadFromStorage');

    this.getAddressBookWrapper();
    // Get RICOH warehouse address from the AddressBook lookup
    // Think to do it different way (use app state?)
    if (this.user.customerCode === 'RICOH') {
      this.whList['KK Ricoh'] = 'RIC';
      getAddressLookupData('RICOH').then((res: AddressBookLookupData[]) => {
        const wh = res.find(x => x.name === 'Ricoh Australia');
        if (wh) {
          this.ricohWarehouseAddress = wh;
        }
      });
    }
    // Request data if we know CC
    if (!this.$ability.can('staff', 'Job')) {
      this.headerFilters.customerCode = this.user.customerCode;
      this.getQuantWrapper();
    }
  }
}
